// ---------------------------------------------------------------------------
// Lightweight event bus that lets sibling UI (e.g. ScoreBreakdown) ask the
// map to temporarily highlight a stretch of polyline + a set of POIs.
//
// Decoupled by design: the map subscribes once, callers fire-and-forget.
// Auto-clears after `ttlMs` so the highlight is always a transient hint,
// never a persistent overlay competing with the regular route layers.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/utils/polyline";

export interface MapHighlightPoi {
  point: LngLat;
  name?: string;
}

export interface MapHighlightPayload {
  /** Polyline coordinates to draw as the amber highlight (a slice of the
   *  selected route). Omit to highlight only POIs. */
  coords?: LngLat[];
  /** POIs to pulse on top of the map. */
  pois?: MapHighlightPoi[];
  /** Short label shown on the map's "highlighting…" toast pill. */
  label?: string;
  /** How long the highlight stays before auto-clearing. */
  ttlMs?: number;
  /** When false, skip the camera fit (useful for chip-level micro-highlights). */
  fit?: boolean;
}

type Handler = (p: MapHighlightPayload | null) => void;
const listeners = new Set<Handler>();

export function subscribeMapHighlight(h: Handler): () => void {
  listeners.add(h);
  return () => { listeners.delete(h); };
}

export function requestMapHighlight(p: MapHighlightPayload): void {
  listeners.forEach((h) => h(p));
}

export function clearMapHighlight(): void {
  listeners.forEach((h) => h(null));
}
